import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../firebase";
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from "firebase/firestore";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import {
    ArrowLeft,
    Save,
    Loader,
    MapPin,
    Phone,
    Clock,
    Image as ImageIcon,
    Search,
    Navigation,
    Store,
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = "AIzaSyBX4NIqDrL7SKaS8q0O1l89hifc3wZSvxo"; // Tu API key de Firebase

const StoreEditor = () => {
    const navigate = useNavigate();
    const { storeId } = useParams();
    const isEditing = !!storeId;

    const [loading, setLoading] = useState(isEditing);
    const [saving, setSaving] = useState(false);
    const [searchingAddress, setSearchingAddress] = useState(false);

    const [formData, setFormData] = useState({
        storeName: "",
        address: "",
        phoneNumber: "",
        latitude: "19.4326", // Ciudad de México default
        longitude: "-99.1332",
        openingTime: "10:00",
        closingTime: "22:00",
        isActive: true,
        photo: "",
    });

    const [mapCenter, setMapCenter] = useState({
        lat: 19.4326,
        lng: -99.1332,
    });

    const [marker, setMarker] = useState(null);

    useEffect(() => {
        if (isEditing) {
            fetchStore();
        }
    }, [storeId]);

    useEffect(() => {
        // Actualizar mapa cuando cambien las coordenadas
        if (formData.latitude && formData.longitude) {
            const lat = parseFloat(formData.latitude);
            const lng = parseFloat(formData.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                setMapCenter({ lat, lng });
                setMarker({ lat, lng });
            }
        }
    }, [formData.latitude, formData.longitude]);

    const fetchStore = async () => {
        try {
            const storeDoc = await getDoc(doc(db, "stores", storeId));
            if (storeDoc.exists()) {
                const data = storeDoc.data();
                setFormData({
                    storeName: data.storeName || "",
                    address: data.address || "",
                    phoneNumber: data.phoneNumber || "",
                    latitude: data.latitude?.toString() || "19.4326",
                    longitude: data.longitude?.toString() || "-99.1332",
                    openingTime: data.openingTime || "10:00",
                    closingTime: data.closingTime || "22:00",
                    isActive: data.isActive !== false,
                    photo: data.photo || "",
                });
            } else {
                alert("Sucursal no encontrada");
                navigate("/admin");
            }
        } catch (error) {
            console.error("Error fetching store:", error);
            alert("Error al cargar la sucursal");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleMapClick = (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setFormData((prev) => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
        }));
        setMarker({ lat, lng });
    };

    const searchAddress = async () => {
        if (!formData.address.trim()) {
            alert("Ingresa una dirección para buscar");
            return;
        }

        setSearchingAddress(true);
        try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: formData.address }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const location = results[0].geometry.location;
                    const lat = location.lat();
                    const lng = location.lng();

                    setFormData((prev) => ({
                        ...prev,
                        latitude: lat.toFixed(6),
                        longitude: lng.toFixed(6),
                        address: results[0].formatted_address,
                    }));

                    setMapCenter({ lat, lng });
                    setMarker({ lat, lng });
                } else {
                    alert("No se pudo encontrar la dirección. Intenta con otra búsqueda.");
                }
                setSearchingAddress(false);
            });
        } catch (error) {
            console.error("Error searching address:", error);
            alert("Error al buscar la dirección");
            setSearchingAddress(false);
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    setFormData((prev) => ({
                        ...prev,
                        latitude: lat.toFixed(6),
                        longitude: lng.toFixed(6),
                    }));
                    setMapCenter({ lat, lng });
                    setMarker({ lat, lng });
                },
                (error) => {
                    alert("No se pudo obtener tu ubicación");
                }
            );
        } else {
            alert("Tu navegador no soporta geolocalización");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones
        if (!formData.storeName.trim()) {
            alert("El nombre de la sucursal es obligatorio");
            return;
        }
        if (!formData.address.trim()) {
            alert("La dirección es obligatoria");
            return;
        }

        setSaving(true);
        try {
            const storeData = {
                ...formData,
                latitude: parseFloat(formData.latitude) || 0,
                longitude: parseFloat(formData.longitude) || 0,
                updatedAt: serverTimestamp(),
            };

            if (isEditing) {
                await updateDoc(doc(db, "stores", storeId), storeData);
                alert("Sucursal actualizada exitosamente");
            } else {
                storeData.createdAt = serverTimestamp();
                await addDoc(collection(db, "stores"), storeData);
                alert("Sucursal creada exitosamente");
            }

            navigate("/admin");
        } catch (error) {
            console.error("Error saving store:", error);
            alert("Error al guardar la sucursal");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-[#FF0000] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando sucursal...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate("/admin")}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#FF0000] transition-colors mb-4"
                    >
                        <ArrowLeft className="w-5 h-5" strokeWidth={2} />
                        <span className="font-medium">Volver al panel</span>
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="bg-[#FF0000] p-3 rounded-xl">
                            <Store className="w-8 h-8 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                {isEditing ? "Editar Sucursal" : "Nueva Sucursal"}
                            </h1>
                            <p className="text-gray-500 mt-1">
                                {isEditing
                                    ? "Actualiza la información de la sucursal"
                                    : "Agrega una nueva ubicación de Pollo Feliz"}
                            </p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Form */}
                        <div className="space-y-6">
                            {/* Información Básica */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">
                                    Información Básica
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Nombre de la Sucursal *
                                        </label>
                                        <input
                                            type="text"
                                            name="storeName"
                                            value={formData.storeName}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                            placeholder="Ej: Pollo Feliz Centro"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Teléfono
                                        </label>
                                        <div className="relative">
                                            <Phone
                                                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                                                strokeWidth={2}
                                            />
                                            <input
                                                type="text"
                                                name="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                                placeholder="(555) 123-4567"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                <Clock className="w-4 h-4 inline mr-1" strokeWidth={2} />
                                                Hora de Apertura
                                            </label>
                                            <input
                                                type="time"
                                                name="openingTime"
                                                value={formData.openingTime}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                <Clock className="w-4 h-4 inline mr-1" strokeWidth={2} />
                                                Hora de Cierre
                                            </label>
                                            <input
                                                type="time"
                                                name="closingTime"
                                                value={formData.closingTime}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 pt-2">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                            className="w-5 h-5 text-[#FF0000] rounded focus:ring-[#FF0000]"
                                        />
                                        <label className="text-sm font-semibold text-gray-700">
                                            Sucursal Activa
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Ubicación */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">
                                    <MapPin className="w-5 h-5 inline mr-2" strokeWidth={2} />
                                    Ubicación
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Dirección *
                                        </label>
                                        <div className="flex gap-2">
                                            <textarea
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                rows="3"
                                                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                                placeholder="Calle, número, colonia, ciudad..."
                                                required
                                            />
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                type="button"
                                                onClick={searchAddress}
                                                disabled={searchingAddress}
                                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                                            >
                                                {searchingAddress ? (
                                                    <>
                                                        <Loader className="w-4 h-4 animate-spin" />
                                                        Buscando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search className="w-4 h-4" strokeWidth={2} />
                                                        Buscar en Mapa
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={getCurrentLocation}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                            >
                                                <Navigation className="w-4 h-4" strokeWidth={2} />
                                                Mi Ubicación
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Latitud
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="latitude"
                                                value={formData.latitude}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                                placeholder="19.4326"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Longitud
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                name="longitude"
                                                value={formData.longitude}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                                placeholder="-99.1332"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Foto */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">
                                    <ImageIcon className="w-5 h-5 inline mr-2" strokeWidth={2} />
                                    Foto de la Sucursal
                                </h2>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            URL de la Imagen
                                        </label>
                                        <input
                                            type="text"
                                            name="photo"
                                            value={formData.photo}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                            placeholder="https://ejemplo.com/imagen.jpg"
                                        />
                                    </div>

                                    {/* Image Preview */}
                                    {formData.photo && (
                                        <div className="relative">
                                            <p className="text-sm font-semibold text-gray-700 mb-2">
                                                Vista Previa:
                                            </p>
                                            <div className="relative rounded-xl overflow-hidden border-2 border-gray-200">
                                                <img
                                                    src={formData.photo}
                                                    alt="Preview"
                                                    className="w-full h-48 object-cover"
                                                    onError={(e) => {
                                                        e.target.src =
                                                            "https://placehold.co/600x400/f0f0f0/666666?text=Error+al+cargar+imagen";
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Map */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-6">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">
                                    Mapa Interactivo
                                </h2>
                                <p className="text-sm text-gray-600 mb-4">
                                    Haz clic en el mapa para seleccionar la ubicación exacta
                                </p>

                                <div className="rounded-xl overflow-hidden border-2 border-gray-200">
                                    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                                        <GoogleMap
                                            mapContainerStyle={{
                                                width: "100%",
                                                height: "500px",
                                            }}
                                            center={mapCenter}
                                            zoom={15}
                                            onClick={handleMapClick}
                                        >
                                            {marker && <Marker position={marker} />}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>

                                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                                    <p className="text-sm text-blue-900 font-semibold mb-2">
                                        💡 Tips para ubicar tu sucursal:
                                    </p>
                                    <ul className="text-sm text-blue-800 space-y-1">
                                        <li>• Escribe la dirección y busca en el mapa</li>
                                        <li>• Haz clic en el mapa para ajustar la ubicación</li>
                                        <li>• Usa "Mi Ubicación" si estás en la sucursal</li>
                                        <li>• Las coordenadas se actualizan automáticamente</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4 pt-6">
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            className="flex-1 bg-gray-100 text-gray-700 px-6 py-4 rounded-xl font-bold text-lg hover:bg-gray-200 transition-colors"
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="flex-1 bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white px-6 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Loader className="w-6 h-6 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-6 h-6" strokeWidth={2} />
                                    {isEditing ? "Actualizar Sucursal" : "Crear Sucursal"}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StoreEditor;


